import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Field, TextArea, Skeleton } from "@/components/admin/Field";

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/settings")
      .then(({ data }) => setSettings(data))
      .catch(() => toast.error("Failed to load settings"))
      .finally(() => setLoading(false));
  }, []);

  function update(k, v) {
    setSettings((s) => ({ ...s, [k]: v }));
  }

  function updateSocial(k, v) {
    setSettings((s) => ({ ...s, social_links: { ...(s?.social_links || {}), [k]: v } }));
  }

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", {
        site_name: settings.site_name,
        hero_eyebrow: settings.hero_eyebrow,
        hero_headline: settings.hero_headline,
        hero_subtitle: settings.hero_subtitle,
        hero_cta_text: settings.hero_cta_text,
        hero_secondary_cta_text: settings.hero_secondary_cta_text,
        contact_email: settings.contact_email,
        contact_phone: settings.contact_phone,
        social_links: settings.social_links,
        footer_text: settings.footer_text,
        seo_title: settings.seo_title,
        seo_description: settings.seo_description,
      });
      setSettings(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (loading || !settings) {
    return (
      <div className="px-6 py-8 md:px-10 md:py-12">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="mt-4 h-72" />
      </div>
    );
  }

  return (
    <div className="px-6 py-8 md:px-10 md:py-12">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="font-body text-[10px] tracking-[0.3em] uppercase text-[#0055ff]">Site Settings</span>
          <h1 className="mt-1 font-display text-3xl font-medium tracking-tight text-white md:text-4xl">Studio identity</h1>
          <p className="mt-1 font-body text-sm text-white/50">Everything text-level on the public homepage.</p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="settings-save-button"
          className="inline-flex items-center gap-2 rounded-full bg-[#0055ff] px-5 py-2.5 font-body text-sm font-medium text-white transition hover:bg-[#1a66ff] disabled:opacity-60"
        >
          <Save className="h-4 w-4" />
          {saving ? "Saving…" : "Save settings"}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Section title="General">
          <Field label="Site name" value={settings.site_name} onChange={(v) => update("site_name", v)} testid="settings-site-name" />
          <Field label="Contact email" value={settings.contact_email} onChange={(v) => update("contact_email", v)} testid="settings-contact-email" />
          <Field label="Phone (optional)" value={settings.contact_phone} onChange={(v) => update("contact_phone", v)} />
          <Field label="Footer text" value={settings.footer_text} onChange={(v) => update("footer_text", v)} />
        </Section>

        <Section title="Hero">
          <Field label="Eyebrow" value={settings.hero_eyebrow} onChange={(v) => update("hero_eyebrow", v)} />
          <Field label="Headline" value={settings.hero_headline} onChange={(v) => update("hero_headline", v)} testid="settings-hero-headline" />
          <TextArea label="Subtitle" value={settings.hero_subtitle} onChange={(v) => update("hero_subtitle", v)} rows={3} />
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Primary CTA" value={settings.hero_cta_text} onChange={(v) => update("hero_cta_text", v)} />
            <Field label="Secondary CTA" value={settings.hero_secondary_cta_text} onChange={(v) => update("hero_secondary_cta_text", v)} />
          </div>
        </Section>

        <Section title="SEO">
          <Field label="SEO title" value={settings.seo_title} onChange={(v) => update("seo_title", v)} testid="settings-seo-title" />
          <TextArea label="SEO description" value={settings.seo_description} onChange={(v) => update("seo_description", v)} rows={3} />
        </Section>

        <Section title="Social links">
          {["instagram", "linkedin", "twitter", "dribbble", "behance", "github"].map((k) => (
            <Field
              key={k}
              label={k}
              value={settings.social_links?.[k] || ""}
              onChange={(v) => updateSocial(k, v)}
              placeholder={`https://${k}.com/parabolum`}
              testid={`settings-social-${k}`}
            />
          ))}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-3xl border border-white/[0.07] bg-white/[0.015] p-6 md:p-7">
      <h2 className="mb-5 font-display text-base font-medium tracking-tight text-white/90">{title}</h2>
      <div className="flex flex-col gap-4">{children}</div>
    </section>
  );
}
